import { useState, useEffect } from 'react';
import apiClient from '@/lib/api/client';

interface Supplier {
  id: number;
  name: string;
  email: string;
  contact_name: string | null;
}

interface Row {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
}

interface Props {
  jobId: string;
  estimateId?: number | null;
  onClose: () => void;
  onSaved: () => void;
}

export const EstimateBuilderModal = ({ jobId, estimateId, onClose, onSaved }: Props) => {
  const [title, setTitle] = useState('Material Estimate');
  const [rows, setRows] = useState<Row[]>([{ id: `r-${Date.now()}`, product_name: '', quantity: 1, unit_price: 0 }]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [showRfq, setShowRfq] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<Set<number>>(new Set());
  const [customEmail, setCustomEmail] = useState('');
  const [rfqMessage, setRfqMessage] = useState('');
  const [sendingRfq, setSendingRfq] = useState(false);
  const [rfqResult, setRfqResult] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (estimateId) loadEstimate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId]);

  useEffect(() => {
    apiClient.get('/catalog/suppliers')
      .then(r => setSuppliers((r.data?.data ?? r.data ?? []).filter((s: Supplier) => s.email)))
      .catch(() => {});
  }, []);

  const loadEstimate = async () => {
    try {
      setLoading(true);
      const resp = await apiClient.get(`/estimates/${estimateId}`);
      const data = resp.data;
      setTitle(data.title ?? 'Material Estimate');
      const itemRows = ((data.items ?? []) as any[])
        .filter((i: any) => i.item_type !== 'heading')
        .map((i: any, idx: number) => ({
          id: `r-${idx}`,
          product_name: i.product_name ?? '',
          quantity: Number(i.quantity) || 0,
          unit_price: Number(i.unit_price) || 0,
        }));
      setRows(itemRows.length > 0 ? itemRows : [{ id: `r-0`, product_name: '', quantity: 1, unit_price: 0 }]);
    } catch {
      setFormError('Failed to load estimate.');
    } finally {
      setLoading(false);
    }
  };

  const addRow = () =>
    setRows(r => [...r, { id: `r-${Date.now()}`, product_name: '', quantity: 1, unit_price: 0 }]);

  const deleteRow = (id: string) =>
    setRows(r => r.filter(row => row.id !== id));

  const update = (id: string, field: keyof Row, value: string | number) =>
    setRows(r => r.map(row => row.id === id ? { ...row, [field]: value } : row));

  const total = rows.reduce((s, r) => s + r.quantity * r.unit_price, 0);

  const toggleSupplier = (id: number) =>
    setSelectedSupplierIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleSendRfq = async () => {
    if (!estimateId) { setFormError('Save the estimate before sending an RFQ.'); return; }
    const selected = suppliers
      .filter(s => selectedSupplierIds.has(s.id))
      .map(s => ({ name: s.name, email: s.email }));
    const trimmed = customEmail.trim();
    if (trimmed) {
      const emails = trimmed.split(/[\s,;]+/).filter(Boolean);
      emails.forEach(e => selected.push({ name: e, email: e }));
    }
    if (selected.length === 0) { setFormError('Select at least one supplier or enter a custom email.'); return; }
    try {
      setSendingRfq(true);
      setRfqResult(null);
      const resp = await apiClient.post(`/estimates/${estimateId}/rfq`, {
        suppliers: selected,
        message: rfqMessage.trim() || undefined,
      });
      const results: Array<{ email: string; sent: boolean; reason?: string }> = resp.data.results ?? [];
      const sentCount = results.filter(r => r.sent).length;
      setRfqResult(`RFQ sent to ${sentCount} of ${results.length} supplier(s).`);
      setSelectedSupplierIds(new Set());
      setCustomEmail('');
      setRfqMessage('');
    } catch {
      setRfqResult('Failed to send RFQ. Please try again.');
    } finally {
      setSendingRfq(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) { setFormError('Title is required.'); return; }
    const validRows = rows.filter(r => r.product_name.trim());
    if (validRows.length === 0) { setFormError('Add at least one item with a name.'); return; }
    setFormError('');
    try {
      setSaving(true);
      const items = validRows.map((r, idx) => ({
        product_id: null,
        product_name: r.product_name,
        quantity: r.quantity,
        unit_price: r.unit_price,
        total: r.quantity * r.unit_price,
        item_type: 'item',
        heading_level: 1,
        sort_order: idx,
      }));
      if (estimateId) {
        await apiClient.put(`/estimates/${estimateId}`, { title, items, total });
      } else {
        await apiClient.post('/estimates', { title, job_id: Number(jobId), items, total });
      }
      onSaved();
      onClose();
    } catch {
      setFormError('Failed to save estimate.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-black text-gray-900">
            {estimateId ? 'Edit Estimate' : 'New Material Estimate'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="p-6 flex flex-col gap-5">
            {formError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{formError}</div>
            )}
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Estimate Title</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            {/* Items */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase">Item Name</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase w-20">Qty</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase w-24">Unit Price</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase w-24">Total</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(row => (
                    <tr key={row.id}>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={row.product_name}
                          onChange={e => update(row.id, 'product_name', e.target.value)}
                          placeholder="Item name"
                          className="w-full px-2 py-1 rounded border border-gray-200 text-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-primary/40"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={row.quantity}
                          onChange={e => update(row.id, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 rounded border border-gray-200 text-right text-sm text-gray-900"
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={row.unit_price}
                          onChange={e => update(row.id, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 rounded border border-gray-200 text-right text-sm text-gray-900"
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900 whitespace-nowrap">
                        ${(row.quantity * row.unit_price).toFixed(2)}
                      </td>
                      <td className="px-2">
                        <button onClick={() => deleteRow(row.id)} className="text-gray-400 hover:text-red-500">
                          <span className="material-symbols-outlined text-base">delete</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                <button
                  onClick={addRow}
                  className="flex items-center gap-1 text-sm text-primary font-medium hover:underline"
                >
                  <span className="material-symbols-outlined text-base">add</span>
                  Add Row
                </button>
                <p className="text-sm font-bold text-gray-900">
                  Total: <span className="text-primary">${total.toFixed(2)}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* RFQ Panel */}
        {!loading && (
          <div className="border-t border-gray-200">
            <button
              onClick={() => setShowRfq(v => !v)}
              className="w-full flex items-center justify-between px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined text-base text-primary">send</span>
                Send RFQ to Suppliers
              </span>
              <span className="material-symbols-outlined text-base text-gray-400">
                {showRfq ? 'expand_less' : 'expand_more'}
              </span>
            </button>

            {showRfq && (
              <div className="px-6 pb-5 flex flex-col gap-4">
                {suppliers.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Catalog Suppliers</p>
                    <div className="flex flex-col gap-1.5 max-h-36 overflow-y-auto">
                      {suppliers.map(s => (
                        <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedSupplierIds.has(s.id)}
                            onChange={() => toggleSupplier(s.id)}
                            className="accent-primary"
                          />
                          <span className="text-gray-900 font-medium">{s.name}</span>
                          <span className="text-gray-400">{s.email}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    Additional emails (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={customEmail}
                    onChange={e => setCustomEmail(e.target.value)}
                    placeholder="supplier@example.com, another@example.com"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    Message (optional)
                  </label>
                  <textarea
                    value={rfqMessage}
                    onChange={e => setRfqMessage(e.target.value)}
                    rows={2}
                    placeholder="Any special instructions or notes for suppliers…"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>

                {rfqResult && (
                  <p className={`text-sm font-medium ${rfqResult.startsWith('Failed') ? 'text-red-600' : 'text-green-700'}`}>
                    {rfqResult}
                  </p>
                )}

                <button
                  onClick={handleSendRfq}
                  disabled={sendingRfq}
                  className="self-start flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-bold disabled:opacity-50"
                >
                  <span className="material-symbols-outlined text-base">send</span>
                  {sendingRfq ? 'Sending…' : 'Send RFQ'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : estimateId ? 'Update Estimate' : 'Save Estimate'}
          </button>
        </div>
      </div>
    </div>
  );
};
